import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Button, Card, CardContent, Chip, Typography, Grid, makeStyles } from '@material-ui/core';

const styles = makeStyles((theme) => ({
  card: {
    paddingBottom: theme.spacing(1),
    position: 'relative',
  },
  error: {
    color: theme.palette.error.main,
  },
  grid: {
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  printButton: {
    position: 'absolute',
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    display: 'none',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  chipContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1),
  },
}));

function getTotalErsattning(user) {
  const classes = styles();
  try {
    const ersattning = user.totalErsattning(user);
    return ersattning.map((ers) => `${ers.sum} ${ers.currency}`).join(', ');
  } catch (error) {
    return (
      <span className={classes.error}>
        <FormattedMessage
          id="dac7.error.total.payout"
          defaultMessage="Error calculating total payout"
        />
      </span>
    );
  }
}

function getTotalAvgifter(user) {
  const classes = styles();
  try {
    const avgifter = user.totalAvgifter(user);
    return avgifter.map((avgift) => `${avgift.sum} ${avgift.currency}`).join(', ');
  } catch (error) {
    return (
      <span className={classes.error}>
        <FormattedMessage id="dac7.error.total.fee" defaultMessage="Error calculating total fee" />
      </span>
    );
  }
}

// eslint-disable-next-line no-unused-vars
const Dac7Data = ({ userData, xmlData }) => {
  const classes = styles();

  const handlePrint = () => {
    window.print();
  };

  if (!Array.isArray(userData) || userData.length === 0) {
    return (
      <Typography variant="subtitle2" className={classes.grid}>
        <FormattedMessage id="dac7.missing.data" defaultMessage="No data" />
      </Typography>
    );
  }

  return (
    <Grid container spacing={1}>
      {userData.map((user, index) => (
        <Grid item key={index} xs={12} sm={6} md={6}>
          <Card className={classes.card}>
            <CardContent>
              <div className={classes.headerContainer}>
                <Typography variant="h6" gutterBottom>
                  <FormattedMessage
                    id="dac7.cottage"
                    defaultMessage="Accommodation #{cottageid}"
                    values={{
                      cottageid: user.cottageId,
                    }}
                  />
                </Typography>
                <div className={classes.chipContainer}>
                  <Chip
                    label={
                      user.userType === 'LEGAL' ? (
                        <FormattedMessage id="Company" defaultMessage="Company" />
                      ) : (
                        <FormattedMessage id="Private person" defaultMessage="Private Person" />
                      )
                    }
                    color={user.userType === 'LEGAL' ? 'primary' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                </div>
              </div>
              <Typography variant="subtitle2">
                <FormattedMessage id="dac7.cottage.address" defaultMessage="Address:" />
              </Typography>
              <Typography variant="body1">{user.propertyStreetAddress}</Typography>
              <Typography variant="body1">
                {user.propertyPostalCode}&nbsp;{user.propertyCity}
              </Typography>
              <Typography variant="subtitle2">
                <FormattedMessage
                  id="dac7.cottage.property_designation"
                  defaultMessage="Property designation:"
                />
              </Typography>
              <Typography variant="body1">{user.propertyDesignation}</Typography>
              <hr />
              <Typography variant="subtitle2">{user.nameOrCompanyName(user)}</Typography>
              <Typography variant="body1">
                <FormattedMessage
                  id="dac7.total.payouts"
                  defaultMessage="Total payout: {amount}"
                  values={{
                    amount: getTotalErsattning(user),
                  }}
                />
              </Typography>
              <Typography variant="body1">
                <FormattedMessage
                  id="dac7.total.fees"
                  defaultMessage="Total fees: {amount}"
                  values={{
                    amount: getTotalAvgifter(user),
                  }}
                />
              </Typography>
              <Typography variant="body1">
                <FormattedMessage
                  id="dac7.total.days"
                  defaultMessage="Total rented days: {amount}"
                  values={{
                    amount: user.antalUthyrdaDagar,
                  }}
                />
              </Typography>
              <Typography variant="body1">
                <FormattedMessage
                  id="dac7.total.transactions"
                  defaultMessage="Number of transactions: {amount}"
                  values={{
                    amount: user.totalAntalTransaktioner(user),
                  }}
                />
              </Typography>
              <Button
                variant="contained"
                color="primary"
                className={classes.printButton}
                onClick={handlePrint}
              >
                <FormattedMessage id="dac7.print" defaultMessage="Print" />
              </Button>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

Dac7Data.propTypes = {
  userData: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.object)]).isRequired,
  xmlData: PropTypes.string,
};

export default Dac7Data;
